import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { Link } from "react-router";

import { api, money, type ProjectSummary, type ServiceBody } from "../api";
import { MODE_MEANING } from "./modes";

/**
 * Registering and configuring projects.
 *
 * **A project is registered here, not by committing a file to its repository.**
 * That is what "independent" means: the stack, the Sentry coordinates, the build
 * and test commands and the JDK live in the platform database. A `.autoheal.yml`
 * is still read when the repository has one, and still owns the safety rails —
 * protected paths, diff ceilings, thresholds — because those must travel with
 * the code they protect.
 */
export function Projects() {
  const projects = useQuery({ queryKey: ["projects"], queryFn: api.projects });
  const [registering, setRegistering] = useState(false);

  if (projects.isPending) return <p className="muted">Loading projects…</p>;
  if (projects.error) return <p className="error">{projects.error.message}</p>;

  return (
    <article>
      <div className="head">
        <h1>Projects</h1>
        <button type="button" onClick={() => setRegistering(!registering)}>
          {registering ? "Cancel" : "Register a repository"}
        </button>
      </div>

      {registering ? <RegisterForm onDone={() => setRegistering(false)} /> : null}

      {projects.data.length === 0 && !registering ? (
        <p className="muted">
          Nothing registered yet. A project needs no commit to its repository —
          everything the engine needs is set here.
        </p>
      ) : null}

      {projects.data.map((project) => (
        <ProjectCard key={project.id} project={project} />
      ))}
    </article>
  );
}

function ProjectCard({ project }: { project: ProjectSummary }) {
  const queries = useQueryClient();
  const [open, setOpen] = useState(false);
  const patch = useMutation({
    mutationFn: (body: Parameters<typeof api.updateProject>[1]) =>
      api.updateProject(project.id, body),
    onSuccess: () => queries.invalidateQueries({ queryKey: ["projects"] }),
  });

  return (
    <section className="attempt">
      <header>
        <button type="button" className="disclose" onClick={() => setOpen(!open)}>
          {open ? "▾" : "▸"}
        </button>
        <Link to={`/projects/${project.id}`}>
          <code>{project.repo}</code>
        </Link>
        {project.forge_provider ? (
          <span className="badge">{PROVIDER_LABEL[project.forge_provider] ?? project.forge_provider}</span>
        ) : null}
        <span className={`status status-mode-${project.write_mode}`}>{project.write_mode}</span>
        {!project.enabled ? <span className="badge">disabled</span> : null}
        {!project.has_forge_token ? (
          // Said here because the failure it causes — a push that cannot
          // authenticate — surfaces at the very end of an attempt.
          <span className="badge">no forge token</span>
        ) : null}
        <span className="num">{money(project.nightly_budget_usd)}/night</span>
      </header>
      <p className="muted">
        {project.repo_url ?? "(deployment-wide forge)"} · {project.default_branch} ·{" "}
        {project.services.length} service(s)
        {project.forge_token_hint ? ` · token ${project.forge_token_hint}` : ""}
        <br />
        {MODE_MEANING[project.write_mode]}
      </p>

      {open ? (
        <div className="panel">
          <h3>Services</h3>
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Stack</th>
                <th>Sentry</th>
              </tr>
            </thead>
            <tbody>
              {project.services.map((service) => (
                <tr key={service.id}>
                  <td>{service.name}</td>
                  <td>{service.stack}</td>
                  <td>
                    {service.sentry_org}/{service.sentry_project}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <h3>Write mode</h3>
          <p className="muted">
            What a live run is permitted to do to this repository. New projects
            start in <code>observe</code>: committing an <code>.autoheal.yml</code>{" "}
            used to prove someone with write access had consented, and this
            replaces that rather than dropping it.
          </p>
          <div className="modes">
            {(["observe", "propose", "push"] as const).map((mode) => (
              <label key={mode}>
                <input
                  type="radio"
                  checked={project.write_mode === mode}
                  disabled={patch.isPending}
                  onChange={() => patch.mutate({ write_mode: mode })}
                />
                <code>{mode}</code> — {MODE_MEANING[mode]}
              </label>
            ))}
          </div>

          <h3>Budget and caps</h3>
          <NumberField
            label="Nightly budget (USD)"
            value={Number.parseFloat(project.nightly_budget_usd)}
            step={0.5}
            onCommit={(v) => patch.mutate({ nightly_budget_usd: v })}
          />
          <NumberField
            label="Issues per night"
            value={project.max_issues_per_night}
            step={1}
            onCommit={(v) => patch.mutate({ max_issues_per_night: v })}
          />

          <h3>Access token</h3>
          <TokenField project={project} />

          <h3>Enabled</h3>
          <label className="live">
            {/* Disabled while pending, like the write_mode radios above. Driven
                by server data, so without the guard React re-renders the box
                back to its old value the instant it is clicked — which reads as
                a click that did not register, and invites a second one on the
                control that stops a project mid-push. */}
            <input
              type="checkbox"
              checked={project.enabled}
              disabled={patch.isPending}
              onChange={(e) => patch.mutate({ enabled: e.target.checked })}
            />
            Accept new runs
          </label>
          <p className="muted">
            Disabling refuses <em>new</em> work. It deliberately does not cancel
            attempts already in flight — killing a worker mid-fix leaves an
            uncommitted branch.
          </p>

          {patch.error ? <p className="error">{patch.error.message}</p> : null}
        </div>
      ) : null}
    </section>
  );
}

function TokenField({ project }: { project: ProjectSummary }) {
  const queries = useQueryClient();
  const [draft, setDraft] = useState("");
  const save = useMutation({
    mutationFn: () => api.updateProject(project.id, { forge_token: draft.trim() }),
    onSuccess: () => {
      setDraft("");
      queries.invalidateQueries({ queryKey: ["projects"] });
    },
  });

  return (
    <div>
      <p className="muted">
        {project.forge_token_hint
          ? `A token ending ${project.forge_token_hint} is stored. Entering a new one replaces it.`
          : "No token stored. Private repositories cannot be cloned without one."}
      </p>
      <form
        className="fields"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <label className="wide">
          New token
          <input
            type="password"
            value={draft}
            autoComplete="off"
            onChange={(e) => setDraft(e.target.value)}
          />
        </label>
        <button type="submit" disabled={!draft.trim() || save.isPending}>
          {save.isPending ? "Saving…" : "Replace"}
        </button>
      </form>
      {save.error ? <p className="error">{save.error.message}</p> : null}
    </div>
  );
}

function NumberField({
  label,
  value,
  step,
  onCommit,
}: {
  label: string;
  value: number;
  step: number;
  onCommit: (value: number) => void;
}) {
  const [draft, setDraft] = useState(String(value));
  // **Resynchronise when the server's value moves.** The draft was seeded once
  // and never reconciled, and this component is never remounted — so a PATCH
  // that was refused, or one the server clamped to a different number, left the
  // input showing what the operator typed while the card header three lines up
  // showed what the server actually holds. Two budgets on one screen, and the
  // believable-looking one was the rejected one.
  const [seen, setSeen] = useState(value);
  if (seen !== value) {
    setSeen(value);
    setDraft(String(value));
  }
  return (
    <label className="inline">
      {label}
      <input
        type="number"
        step={step}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        // Committed on blur rather than per keystroke: each change is an audit
        // row, and typing "12" should not record setting it to 1.
        onBlur={() => {
          const parsed = Number.parseFloat(draft);
          if (!Number.isNaN(parsed) && parsed !== value) onCommit(parsed);
        }}
      />
    </label>
  );
}

const PROVIDER_LABEL: Record<string, string> = {
  gitlab: "GitLab",
  github: "GitHub",
  gitea: "Gitea",
  forgejo: "Forgejo",
};

const EMPTY_SERVICE: ServiceBody = {
  name: "",
  stack: "java-maven",
  sentry_org: "",
  sentry_project: "",
  build_cmd: "mvn -B -ntp -DskipTests package",
  test_cmd: "mvn -B -ntp test",
  jdk: "17",
  path: ".",
};

/** Pick the Sentry project from what the token can see, not from memory.
 *
 * **The slug is not the repository name.** `ems-core-api` is
 * `ems-core-api` in Sentry, because projects there are named after the
 * product. Typed by hand that mistake is accepted silently and surfaces hours
 * later as a 404 on the Issues tab, which looks exactly like a scope problem.
 *
 * One control sets both coordinates, because the organization is a property of
 * the project, not a second thing to get right.
 *
 * Falls back to two text boxes when Sentry cannot be asked — registration must
 * not be blocked by a credential that is only needed at triage time — and says
 * why, so the fallback does not read as the normal state of the form.
 */
function SentryField({
  service,
  set,
  baseUrl,
}: {
  service: ServiceBody;
  set: (patch: Partial<ServiceBody>) => void;
  baseUrl: string | undefined;
}) {
  const known = useQuery({ queryKey: ["sentry-projects"], queryFn: api.sentryProjects });
  const available = known.data?.projects ?? [];
  const where = baseUrl ? <code>{baseUrl.replace(/^https?:\/\//, "")}</code> : "Sentry";

  if (known.isPending) {
    return (
      <label className="wide">
        Sentry project
        <select disabled>
          <option>Loading projects from Sentry…</option>
        </select>
      </label>
    );
  }

  if (!available.length) {
    const why =
      known.data?.error?.detail ??
      (known.error ? known.error.message : "Sentry returned no projects for this token");
    return (
      <>
        <label>
          Sentry org
          <input
            value={service.sentry_org}
            onChange={(e) => set({ sentry_org: e.target.value })}
            placeholder="example"
            required
          />
        </label>
        <label>
          Sentry project
          <input
            value={service.sentry_project}
            onChange={(e) => set({ sentry_project: e.target.value })}
            placeholder="ems-core-api"
            required
          />
        </label>
        <p className="muted wide">
          <strong>Could not list the projects on {where}</strong>, so these two are
          typed instead: {why}
          <br />
          They are the two path segments of any Sentry URL —{" "}
          <code>/organizations/<b>example</b>/projects/<b>ems-core-api</b>/</code>{" "}
          — or Settings → Projects, where the slug is shown under each project&apos;s
          name. Use the <strong>slug</strong>, not the display name: they differ
          whenever a project has been renamed.
        </p>
      </>
    );
  }

  const selected = service.sentry_project ? `${service.sentry_org}/${service.sentry_project}` : "";

  return (
    <>
      <label className="wide">
        Sentry project
        <select
          value={selected}
          required
          onChange={(e) => {
            const chosen = available.find((p) => `${p.org}/${p.slug}` === e.target.value);
            set({
              sentry_org: chosen?.org ?? "",
              sentry_project: chosen?.slug ?? "",
            });
          }}
        >
          <option value="">Choose a project…</option>
          {available.map((project) => (
            <option key={`${project.org}/${project.slug}`} value={`${project.org}/${project.slug}`}>
              {project.org}/{project.slug}
              {project.platform ? ` — ${project.platform}` : ""}
            </option>
          ))}
        </select>
      </label>
      <p className="muted wide">
        <strong>Where the issues come from.</strong> Everything on this project&apos;s
        Issues tab, and everything a nightly run picks up, is an unresolved issue
        read from the Sentry project you choose here. Pick the one that receives
        errors from <em>this service</em> — one service, one Sentry project.
        <br />
        Listed by their Sentry slug, which is named after the product and so rarely
        matches the repository: <code>ems-core-api</code> is{" "}
        <code>ems-core-api</code>. The platform after each name is Sentry&apos;s
        own, and is the quickest way to tell similar projects apart.
        <br />
        Read from {where} using this deployment&apos;s Sentry token, so only projects
        that token can see are listed. If one is missing, the token needs access to
        it — there is no name you can type here instead.
      </p>
    </>
  );
}

function RegisterForm({ onDone }: { onDone: () => void }) {
  const queries = useQueryClient();
  const options = useQuery({ queryKey: ["options"], queryFn: api.options });
  const [repoUrl, setRepoUrl] = useState("");
  const [provider, setProvider] = useState("gitlab");
  const [token, setToken] = useState("");
  const [branch, setBranch] = useState("main");
  const [budget, setBudget] = useState(5);
  const [cap, setCap] = useState(3);
  const [service, setService] = useState<ServiceBody>(EMPTY_SERVICE);

  const register = useMutation({
    mutationFn: () =>
      api.registerProject({
        repo_url: repoUrl.trim(),
        forge_provider: provider,
        forge_token: token.trim() || null,
        default_branch: branch.trim(),
        nightly_budget_usd: budget,
        max_issues_per_night: cap,
        per_issue_share: 0.5,
        services: [service],
      }),
    onSuccess: () => {
      queries.invalidateQueries({ queryKey: ["projects"] });
      onDone();
    },
  });

  const set = (patch: Partial<ServiceBody>) => setService({ ...service, ...patch });

  return (
    <form
      className="trigger"
      onSubmit={(event) => {
        event.preventDefault();
        register.mutate();
      }}
    >
      <h2>Register a repository</h2>
      <p className="muted">
        No commit to the repository is required. It will start in{" "}
        <code>observe</code> mode.
      </p>

      <div className="fields">
        <label className="wide">
          Repository URL
          {/* One field, not a slug plus a base URL: two can disagree, and the
              disagreement surfaces as a forge 404 that reads exactly like a
              token with insufficient scope. */}
          <input
            type="url"
            value={repoUrl}
            onChange={(e) => setRepoUrl(e.target.value)}
            placeholder="https://forge.example.com/example/ems-core-api.git"
            required
          />
        </label>
        <label>
          Git server
          {/* Asked for, never guessed from the hostname: a host called
              gitlab.example.com proves nothing about what runs on it, and
              guessing wrong 404s every API call. */}
          <select value={provider} onChange={(e) => setProvider(e.target.value)}>
            {(options.data?.providers ?? ["gitlab", "github", "gitea"]).map((name) => (
              <option key={name} value={name}>
                {PROVIDER_LABEL[name] ?? name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Default branch
          <input value={branch} onChange={(e) => setBranch(e.target.value)} required />
        </label>
        <label>
          Nightly budget (USD)
          <input
            type="number"
            step={0.5}
            min={0.5}
            value={budget}
            onChange={(e) => setBudget(Number(e.target.value))}
          />
        </label>
        <label>
          Issues per night
          <input
            type="number"
            min={1}
            value={cap}
            onChange={(e) => setCap(Number(e.target.value))}
          />
        </label>
      </div>

      <div className="fields">
        <label className="wide">
          Access token
          <input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            autoComplete="off"
            placeholder="leave empty for a public repository"
          />
        </label>
      </div>
      <p className="muted">
        Needs <strong>read and write on the repository</strong> and permission to
        open a merge or pull request — that is the whole job: clone, push a
        branch under <code>autoheal/</code>, open the request.
        <br />
        Do not give it admin, and on GitLab do not give it a scope that can edit
        CI configuration: a change request from an agent holding that is
        arbitrary pipeline execution in your group.
        <br />
        Stored encrypted. No page or endpoint will show it again.
        {options.data?.forge_hosts.length ? (
          <>
            <br />
            This deployment can reach: <code>{options.data.forge_hosts.join(", ")}</code>
          </>
        ) : null}
      </p>

      <h3>Service</h3>
      <p className="muted">
        One buildable unit. None of these can be defaulted — the schema requires
        them and forbids guessing a JDK.
      </p>
      <div className="fields">
        <label>
          Name
          <input
            value={service.name}
            onChange={(e) => set({ name: e.target.value })}
            placeholder="core-api"
            required
          />
        </label>
        <label>
          Stack
          <select value={service.stack} onChange={(e) => set({ stack: e.target.value })}>
            {(options.data?.stacks ?? ["java-maven"]).map((stack) => (
              <option key={stack} value={stack}>
                {stack}
              </option>
            ))}
          </select>
        </label>
        <label>
          JDK
          <input value={service.jdk} onChange={(e) => set({ jdk: e.target.value })} required />
        </label>
        <label>
          Path in repo
          <input value={service.path} onChange={(e) => set({ path: e.target.value })} />
        </label>
        <SentryField
          service={service}
          set={set}
          baseUrl={options.data?.sentry_base_url}
        />
      </div>
      <div className="fields">
        <label className="wide">
          Build command
          <input
            value={service.build_cmd}
            onChange={(e) => set({ build_cmd: e.target.value })}
            required
          />
        </label>
        <label className="wide">
          Test command
          <input
            value={service.test_cmd}
            onChange={(e) => set({ test_cmd: e.target.value })}
            required
          />
        </label>
      </div>

      <button type="submit" disabled={register.isPending}>
        {register.isPending ? "Registering…" : "Register"}
      </button>
      {register.error ? <p className="error">{register.error.message}</p> : null}
    </form>
  );
}
